// src/components/Navbar.jsx
import {
  AppBar,
  Toolbar,
  Button,
  IconButton,
  Container,
  Menu,
  MenuItem,
  Box,
  useMediaQuery,
  useTheme,
  Typography,
} from "@mui/material";
import {
  Brightness4 as Brightness4Icon,
  Brightness7 as Brightness7Icon,
  Menu as MenuIcon,
} from "@mui/icons-material";
import { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";

const Navbar = ({ colorMode, mode }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const location = useLocation();
  const navigate = useNavigate();

  const [anchorEl, setAnchorEl] = useState(null);
  const [activeSection, setActiveSection] = useState(null);

  // Keep these ids in sync with Home.jsx
  const navItems = [
    { label: "Home", href: "#home" },
    { label: "Experience", href: "#experience" },
    { label: "Skills", href: "#skills" },
    { label: "Projects", href: "#projects" },
    { label: "Contact", href: "#contact" },
  ];

  const handleMenuOpen = (event) => setAnchorEl(event.currentTarget);
  const handleMenuClose = () => setAnchorEl(null);

  // Smooth-scroll with fixed AppBar offset
  const scrollToHash = (hash) => {
    const id = hash.startsWith("#") ? hash.slice(1) : hash;
    const el = document.getElementById(id);
    if (!el) return;
    const headerOffset = 15; // adjust if your AppBar height differs
    const y = el.getBoundingClientRect().top + window.scrollY - headerOffset;
    window.scrollTo({ top: y, behavior: "smooth" });
  };

  const handleNavClick = (href) => {
    // If not on home, store target and go to home; Home.jsx will scroll on mount
    if (location.pathname !== "/") {
      sessionStorage.setItem("scrollTarget", href.replace("#", ""));
      navigate("/");
    } else {
      scrollToHash(href);
    }
    setActiveSection(href);
    handleMenuClose();
  };

  // Support arriving at "/" with a hash, e.g. "/#about"
  useEffect(() => {
    if (location.pathname === "/" && location.hash) {
      // wait a tick to ensure sections are mounted
      setTimeout(() => scrollToHash(location.hash), 0);
      setActiveSection(location.hash);
    }
  }, [location]);

  // Track active section ONLY on the homepage (where sections exist)
  useEffect(() => {
    if (location.pathname !== "/") {
      setActiveSection(null);
      return;
    }
    const onScroll = () => {
      const scrollY = window.scrollY;

      // Check all nav items to see if current scroll position is within their section
      for (const item of navItems) {
        const el = document.querySelector(item.href);
        if (el) {
          const top = el.offsetTop;
          const height = el.offsetHeight;
          const bottom = top + height;
          const headerOffset = 100;

          // Check if scroll position is within this section (accounting for header)
          if (
            scrollY >= top - headerOffset &&
            scrollY < bottom - headerOffset
          ) {
            setActiveSection(item.href);
            return;
          }
        }
      }

      // If no nav item section is active, clear the active section
      setActiveSection(null);
    };
    window.addEventListener("scroll", onScroll);
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  return (
    <>
      {/* Spacer so content doesn't hide under fixed AppBar */}
      <Box sx={{ height: { xs: "56px", sm: "64px" } }} />
      <AppBar
        position="fixed"
        elevation={0}
        sx={{
          backdropFilter: "blur(20px)",
          WebkitBackdropFilter: "blur(20px)",
          backgroundColor:
            mode === "dark"
              ? "rgba(18, 18, 18, 0.7)"
              : "rgba(255, 255, 255, 0.7)",
          borderBottom: `1px solid ${
            mode === "dark" ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.1)"
          }`,
          zIndex: theme.zIndex.drawer + 1,
        }}
      >
        <Container maxWidth="lg">
          <Toolbar sx={{ minHeight: { xs: "56px", sm: "64px" } }}>
            {/* Logo */}
            <Button
              href="#home"
              onClick={(e) => {
                e.preventDefault();
                if (location.pathname !== "/") {
                  sessionStorage.setItem("scrollTarget", "home");
                  navigate("/");
                } else {
                  window.scrollTo({ top: 0, behavior: "smooth" });
                }
                setActiveSection("#home");
              }}
              sx={{
                textTransform: "none",
                display: "flex",
                alignItems: "center",
                color: mode === "dark" ? "#f5f5f5" : "#1a1a1a",
              }}
            >
              <Box sx={{ height: 42, width: 42, mr: 2 }}>
                <img
                  src="/media/images/pc2.png"
                  alt="Logo"
                  style={{ height: "100%", width: "100%", objectFit: "cover" }}
                />
              </Box>
              <Typography variant="h6" fontWeight="600" letterSpacing="0.5px">
                Shimul
              </Typography>
            </Button>

            <Box sx={{ flexGrow: 1 }} />

            {!isMobile ? (
              <>
                {navItems.map((item) => (
                  <Button
                    key={item.href}
                    onClick={() => handleNavClick(item.href)}
                    sx={{
                      mx: 1,
                      fontWeight:
                        activeSection === item.href ? "bold" : "normal",
                      color:
                        activeSection === item.href
                          ? theme.palette.primary.main
                          : mode === "dark"
                            ? "#fff"
                            : "rgba(0,0,0,0.87)",
                      position: "relative",
                      "&::after": {
                        content: '""',
                        position: "absolute",
                        bottom: 0,
                        left: 0,
                        right: 0,
                        height: 2,
                        backgroundColor: theme.palette.primary.main,
                        transform:
                          activeSection === item.href
                            ? "scaleX(1)"
                            : "scaleX(0)",
                        transition: "transform 0.3s ease",
                        transformOrigin: "center",
                      },
                      "&:hover::after": {
                        transform: "scaleX(1)",
                      },
                    }}
                  >
                    {item.label}
                  </Button>
                ))}
                <IconButton
                  onClick={colorMode.toggleColorMode}
                  sx={{
                    ml: 1,
                    color: mode === "dark" ? "#fff" : "rgba(0, 0, 0, 0.87)",
                    "&:hover": { color: theme.palette.primary.main },
                  }}
                >
                  {mode === "dark" ? <Brightness7Icon /> : <Brightness4Icon />}
                </IconButton>
              </>
            ) : (
              <>
                <IconButton
                  onClick={handleMenuOpen}
                  sx={{
                    color: mode === "dark" ? "#fff" : "rgba(0, 0, 0, 0.87)",
                  }}
                >
                  <MenuIcon />
                </IconButton>
                <Menu
                  anchorEl={anchorEl}
                  open={Boolean(anchorEl)}
                  onClose={handleMenuClose}
                  PaperProps={{
                    sx: {
                      backdropFilter: "blur(20px)",
                      backgroundColor:
                        mode === "dark"
                          ? "rgba(18, 18, 18, 0.9)"
                          : "rgba(255, 255, 255, 0.9)",
                      mt: 1,
                      border: `1px solid ${
                        mode === "dark"
                          ? "rgba(255, 255, 255, 0.1)"
                          : "rgba(0, 0, 0, 0.1)"
                      }`,
                    },
                  }}
                >
                  {navItems.map((item) => (
                    <MenuItem
                      key={item.href}
                      onClick={() => handleNavClick(item.href)}
                      selected={activeSection === item.href}
                      sx={{
                        fontWeight:
                          activeSection === item.href ? "bold" : "normal",
                        color:
                          activeSection === item.href
                            ? theme.palette.primary.main
                            : mode === "dark"
                              ? "#fff"
                              : "rgba(0, 0, 0, 0.87)",
                      }}
                    >
                      {item.label}
                    </MenuItem>
                  ))}
                  <MenuItem onClick={colorMode.toggleColorMode}>
                    {mode === "dark" ? "Light Mode" : "Dark Mode"}
                    <IconButton sx={{ ml: 1 }}>
                      {mode === "dark" ? (
                        <Brightness7Icon />
                      ) : (
                        <Brightness4Icon />
                      )}
                    </IconButton>
                  </MenuItem>
                </Menu>
              </>
            )}
          </Toolbar>
        </Container>
      </AppBar>
    </>
  );
};

export default Navbar;
